import React from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Shield, Lock, Eye, FileText, UserCheck, Mail, Phone, Globe, CheckCircle } from 'lucide-react';
export function PrivacyPolicyPage() {
  const sections = [{
    icon: FileText,
    title: '1. What Personal Information We Collect',
    content: ['Full name and surname', 'Contact details (phone number, email address)', 'Delivery address', 'Order details and preferences', 'Communication history', 'Any information you voluntarily provide to us']
  }, {
    icon: CheckCircle,
    title: '2. Lawful Purpose for Processing',
    content: ['To process and fulfill your orders', 'To communicate with you regarding orders, quotes, and deliveries', 'To provide customer support', 'To maintain business records', 'To improve our services'],
    note: 'Your information is processed lawfully, reasonably, and in a manner that does not infringe your privacy.'
  }, {
    icon: UserCheck,
    title: '3. Consent',
    content: ['By using our services or communicating with us, you consent to the collection and processing of your personal information as described in this policy.', 'You may withdraw your consent at any time, subject to legal and contractual restrictions.']
  }, {
    icon: Eye,
    title: '4. Information Sharing',
    content: ['We do not sell your personal information.', 'We may share your information only with:', '  • Delivery partners (for delivery purposes only)', '  • Payment service providers (if applicable)', 'All third parties are required to respect the confidentiality and security of your information.']
  }, {
    icon: Lock,
    title: '5. Data Security Safeguards',
    content: ['We take appropriate, reasonable technical and organisational measures to:', '  • Prevent loss, damage, or unauthorised access', '  • Prevent unlawful processing of personal information', '  • Protect the integrity and confidentiality of your personal information']
  }];
  return <div className="min-h-screen bg-white" data-id="element-2801">
      <Navbar data-id="element-2802" />

      <main className="pt-24 pb-16" data-id="element-2803">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-rose-50 to-white py-16" data-id="element-2804">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2805">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-2806">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6" data-id="element-2807">
                <Shield className="w-8 h-8 text-rose-600" data-id="element-2808" />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-2809">
                Privacy Policy (POPIA)
              </h1>
              <p className="text-lg text-gray-600 mb-2" data-id="element-2810">
                Bloom and Pop Gift Shop
              </p>
              <p className="text-sm text-gray-500" data-id="element-2811">
                Effective Date: 16 January 2026
              </p>
            </motion.div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2812">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-gradient-to-br from-rose-50 to-purple-50 rounded-2xl p-8 border border-rose-100" data-id="element-2813">
            <p className="text-gray-700 leading-relaxed" data-id="element-2814">
              Bloom and Pop Gift Shop ("we", "us", "our") is committed to
              protecting your personal information in accordance with the{' '}
              <strong data-id="element-2815">
                Protection of Personal Information Act, 4 of 2013 (POPIA)
              </strong>{' '}
              of South Africa.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-2816">
              This Privacy Policy explains how we lawfully collect, use, store,
              and protect your personal information.
            </p>
          </motion.div>
        </section>

        {/* Main Sections */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8" data-id="element-2817">
          {sections.map((section, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 * (index + 3)
        }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-id="element-2818">
              <div className="flex items-start gap-4 mb-4" data-id="element-2819">
                <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2820">
                  <section.icon className="w-5 h-5 text-rose-600" data-id="element-2821" />
                </div>
                <h2 className="font-bold text-xl text-gray-900 flex-1" data-id="element-2822">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-2 ml-14" data-id="element-2823">
                {section.content.map((item, i) => <li key={i} className="text-gray-700 leading-relaxed" data-id="element-2824">
                    {item.startsWith('  •') ? <span className="ml-4" data-id="element-2825">{item}</span> : item}
                  </li>)}
              </ul>
              {section.note && <p className="mt-4 ml-14 text-sm text-gray-600 italic bg-gray-50 p-3 rounded-lg" data-id="element-2826">
                  {section.note}
                </p>}
            </motion.div>)}

          {/* Additional Sections */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-id="element-2827">
            <h2 className="font-bold text-xl text-gray-900 mb-4" data-id="element-2828">
              6. Data Retention
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-2829">
              We will retain your personal information only for as long as
              necessary to fulfill the purposes for which it was collected or as
              required by law.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9
        }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-id="element-2830">
            <h2 className="font-bold text-xl text-gray-900 mb-4" data-id="element-2831">
              7. Your Rights in Terms of POPIA
            </h2>
            <p className="text-gray-700 mb-3" data-id="element-2832">You have the right to:</p>
            <ul className="space-y-2 text-gray-700" data-id="element-2833">
              <li data-id="element-2834">• Request access to your personal information</li>
              <li data-id="element-2835">• Request correction or deletion of your information</li>
              <li data-id="element-2836">• Object to the processing of your personal information</li>
              <li data-id="element-2837">
                • Lodge a complaint with the Information Regulator of South
                Africa
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.0
        }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-id="element-2838">
            <h2 className="font-bold text-xl text-gray-900 mb-4" data-id="element-2839">
              8. Cookies and Website Usage
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-2840">
              Our website may collect limited technical information to improve
              your browsing experience. No sensitive personal data is collected
              without your knowledge.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.1
        }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-id="element-2841">
            <h2 className="font-bold text-xl text-gray-900 mb-4" data-id="element-2842">
              9. Changes to This Policy
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-2843">
              We reserve the right to update this Privacy Policy from time to
              time. Any changes will be published on our platforms.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.2
        }} className="bg-gradient-to-br from-rose-500 to-purple-600 rounded-2xl p-8 text-white" data-id="element-2844">
            <h2 className="font-bold text-2xl mb-4" data-id="element-2845">10. Contact Details</h2>
            <p className="text-rose-50 mb-6" data-id="element-2846">
              If you have any questions about this Privacy Policy or your
              personal information, please contact:
            </p>
            <div className="space-y-3" data-id="element-2847">
              <div className="flex items-center gap-3" data-id="element-2848">
                <Mail className="w-5 h-5 text-rose-200" data-id="element-2849" />
                <a href="mailto:sales@bloomandpop.co.za" className="text-white hover:text-rose-100 transition-colors" data-id="element-2850">
                  sales@bloomandpop.co.za
                </a>
              </div>
              <div className="flex items-center gap-3" data-id="element-2851">
                <Phone className="w-5 h-5 text-rose-200" data-id="element-2852" />
                <a href="tel:0732736906" className="text-white hover:text-rose-100 transition-colors" data-id="element-2853">
                  073 273 6906
                </a>
              </div>
              <div className="flex items-center gap-3" data-id="element-2854">
                <Globe className="w-5 h-5 text-rose-200" data-id="element-2855" />
                <a href="https://www.bloomandpop.co.za" target="_blank" rel="noopener noreferrer" className="text-white hover:text-rose-100 transition-colors" data-id="element-2856">
                  www.bloomandpop.co.za
                </a>
              </div>
            </div>
          </motion.div>

          {/* Legal Compliance Note */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.3
        }} className="bg-gray-50 rounded-2xl p-8 border border-gray-200" data-id="element-2857">
            <div className="flex items-start gap-3 mb-4" data-id="element-2858">
              <Shield className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" data-id="element-2859" />
              <h2 className="font-bold text-xl text-gray-900" data-id="element-2860">
                Legal Compliance Note
              </h2>
            </div>
            <p className="text-gray-700 mb-4" data-id="element-2861">
              This Privacy Policy is designed to align with the POPIA conditions
              for lawful processing, including:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700" data-id="element-2862">
              <div className="flex items-center gap-2" data-id="element-2863">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2864" />
                <span data-id="element-2865">Accountability</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2866">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2867" />
                <span data-id="element-2868">Processing limitation</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2869">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2870" />
                <span data-id="element-2871">Purpose specification</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2872">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2873" />
                <span data-id="element-2874">Further processing limitation</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2875">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2876" />
                <span data-id="element-2877">Information quality</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2878">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2879" />
                <span data-id="element-2880">Openness</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2881">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2882" />
                <span data-id="element-2883">Security safeguards</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2884">
                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2885" />
                <span data-id="element-2886">Data subject participation</span>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-2887" />
    </div>;
}